import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import logo from "@/assets/logo_medium.png";
import { validateCPF, cleanCPF } from "@/lib/cpf-validator";
import { onboardingCopy } from "@/lib/onboarding-copy";
import { supabase } from "@/integrations/supabase/client";
import { OnboardingProgress } from "@/components/OnboardingProgress";
import { OnboardingFooter } from "@/components/OnboardingFooter";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { UpgradeModal } from "@/components/subscription/UpgradeModal";

const Patient = () => {
  const navigate = useNavigate();
  const { canAddPatient, patientsCount } = useSubscriptionStore();
  const [formData, setFormData] = useState({ name: "", cpf: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    // DEPRECATED: Este fluxo está sendo migrado para /onboarding/miafile
    toast.info("⚠️ Redirecionando para nova versão do onboarding...", { duration: 2000 });
    setTimeout(() => navigate("/onboarding/miafile"), 2000);
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.cpf) {
      toast.error(onboardingCopy.patient.errors.fillAll);
      return;
    }

    if (!validateCPF(formData.cpf)) {
      toast.error(onboardingCopy.patient.errors.cpf);
      return;
    }

    // Validar limite de pacientes do plano
    if (!canAddPatient()) {
      setShowUpgradeModal(true);
      return;
    }

    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Usuário não autenticado");
        navigate("/auth");
        return;
      }

      // Create in patients_onboarding
      const { data, error } = await supabase
        .from('patients_onboarding')
        .insert({
          owner_id: user.id,
          full_name: formData.name,
          cpf: cleanCPF(formData.cpf)
        })
        .select()
        .single();

      if (error) {
        console.error("Patient creation error:", error);
        toast.error(error.message);
        setIsLoading(false);
        return;
      }

      // Only patients_onboarding table is used now

      // Atualizar contagem de pacientes no store
      const subscriptionStore = (await import("@/stores/subscriptionStore")).useSubscriptionStore;
      await subscriptionStore.getState().refreshPatientsCount();

      // Update onboarding progress
      await supabase
        .from('profiles')
        .update({ onboarding_step: 3 })
        .eq('id', user.id);

      toast.success(onboardingCopy.patient.success);
      navigate("/onboarding/upload", { state: { patientId: data.id } });
    } catch (error) {
      console.error("Patient creation error:", error);
      toast.error(onboardingCopy.general.error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <div className="w-full max-w-2xl animate-fade-in">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Elinia" className="h-8" />
        </div>

        <OnboardingProgress currentStep={1} totalSteps={3} />

        <Card className="shadow-medium">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-primary mb-2">
                {onboardingCopy.patient.title}
              </h1>
              <p className="text-muted-foreground">
                {onboardingCopy.patient.subtitle}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">{onboardingCopy.patient.fields.name.label}</Label>
                <Input
                  id="name"
                  placeholder={onboardingCopy.patient.fields.name.placeholder}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cpf">{onboardingCopy.patient.fields.cpf.label}</Label>
                <Input
                  id="cpf"
                  placeholder={onboardingCopy.patient.fields.cpf.placeholder}
                  value={formData.cpf}
                  onChange={(e) => setFormData({ ...formData, cpf: e.target.value.replace(/\D/g, '') })}
                  maxLength={11}
                  required
                />
              </div>

              <Button type="submit" className="w-full" size="lg" disabled={isLoading}>
                {isLoading ? onboardingCopy.general.saving : onboardingCopy.patient.button}
              </Button>
            </form>
          </CardContent>
        </Card>

        <OnboardingFooter />
      </div>

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason="patients"
        currentCount={patientsCount}
      />
    </div>
  );
};

export default Patient;
